import type { CalculationType } from 'components/nri-table/types';
import type { UnitInterestTableItem } from './types';
import {
  DataTable,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  DataTableRenderProps,
} from '@carbon/react';
import styles from './total-unit-interest-card.module.scss';
import { BaseCard } from 'components/base-card';
import { CalculationIconTrigger } from 'components/calculation-icon-trigger';
import { Calculation } from '@carbon/react/icons';
import { formatNumberWithTooltip } from 'components/tooltip-number';
import { formatNumber } from 'utils/number';
import { PlainLink } from 'components/link/link';
import { LazyUnitInterestCard } from 'components/lazy-unit-interest-card';
import { GisCalculationCard } from 'components/uat-nri-matrix-card';
import { getSlotTitle, getInterestHeader, formatFactor } from './utils';

const renderCalculationTrigger = (active: boolean, toggle: () => void) => (
  <CalculationIconTrigger
    definition="Show Calculation"
    active={active}
    onClick={toggle}
  >
    <div className={styles.iconContainer}>
      <Calculation aria-hidden="true" role="img" />
    </div>
  </CalculationIconTrigger>
);

interface UnitCalculationContentProps {
  slotName: string;
  items: UnitInterestTableItem[];
  calculatedValue: number;
  calculationType: CalculationType;
  onTractClick?: (tractId: string, subtractId?: string) => void;
  /** Overrides the "Total Slot NRI" / "Total Slot WI" title. */
  title?: string;
  /** What each line's "Show Calculation" explains: the stored calculation
   *  (default), or its GIS row for GIS values beside AM values. */
  lineFormula?: 'stored' | 'gis';
}

const UnitCalculationContent = ({
  slotName,
  items,
  calculatedValue,
  calculationType,
  onTractClick,
  title,
  lineFormula = 'stored',
}: UnitCalculationContentProps) => {
  const headers = [
    { key: 'subtract', header: 'Subtract' },
    { key: 'uat', header: 'UAT' },
    { key: 'value', header: 'Value' },
    { key: 'factor', header: 'Factor' },
    { key: 'uatInterest', header: getInterestHeader(calculationType) },
  ];

  const tableRows = items.map((item, index) => ({
    id: String(index),
    subtract: item.subtract ?? '--',
    uat: item.uat ?? '--',
    value: formatNumber({
      value: item.value,
      minPrecision: 0,
      maxPrecision: 2,
      includeCommas: true,
      unit: item.valueUnit,
    }),
    factor: formatFactor(item.factor),
    uatInterest: item.uatInterest,
  }));

  return (
    <div className={styles.container}>
      <div className={styles.title}>
        {title ?? getSlotTitle(calculationType)}{' '}
        <span className={styles.subtitle}>({slotName})</span>
      </div>
      <div className={styles.tableContainer}>
        <div className={styles.tableWrapper}>
          <DataTable rows={tableRows} headers={headers}>
            {({
              rows,
              headers,
              getTableProps,
              getHeaderProps,
              getRowProps,
            }: DataTableRenderProps<any, any[]>) => (
              <Table {...getTableProps()}>
                <TableHead>
                  <TableRow>
                    {headers.map((header: any) => (
                      <TableHeader
                        {...getHeaderProps({ header })}
                        key={header.key}
                      >
                        {header.header}
                      </TableHeader>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row: any) => {
                    const originalRow = tableRows.find((r) => r.id === row.id);
                    const originalItem = items[Number(row.id)];
                    return (
                      <TableRow {...getRowProps({ row })} key={row.id}>
                        {row.cells.map((cell: any) => (
                          <TableCell key={cell.id}>
                            {cell.info.header === 'subtract' ? (
                              onTractClick &&
                              originalItem?.subtractSalesforceId &&
                              originalItem?.tractSalesforceId ? (
                                <PlainLink
                                  href="#"
                                  className={styles.subtractValue}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    onTractClick(
                                      originalItem.tractSalesforceId!,
                                      originalItem.subtractSalesforceId
                                    );
                                  }}
                                >
                                  {cell.value}
                                </PlainLink>
                              ) : (
                                <span className={styles.subtractValue}>
                                  {cell.value}
                                </span>
                              )
                            ) : cell.info.header === 'uatInterest' ? (
                              <div className={styles.uatInterestCell}>
                                {formatNumberWithTooltip(
                                  originalRow?.uatInterest ?? null,
                                  8
                                )}
                                {originalItem?.nriCalculationId &&
                                  (lineFormula === 'gis' ? (
                                    <GisCalculationCard
                                      nriCalculationId={
                                        originalItem.nriCalculationId
                                      }
                                      calculationType={calculationType}
                                      renderTrigger={renderCalculationTrigger}
                                    />
                                  ) : (
                                    <LazyUnitInterestCard
                                      nriCalculationId={
                                        originalItem.nriCalculationId
                                      }
                                      unitInterestValue={
                                        originalItem.uatInterest ?? null
                                      }
                                      calculationType={calculationType}
                                      renderTrigger={renderCalculationTrigger}
                                    />
                                  ))}
                              </div>
                            ) : (
                              cell.value
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    );
                  })}
                  {/* Total row */}
                  <TableRow className={styles.totalRow}>
                    <TableCell colSpan={headers.length - 1}>Total</TableCell>
                    <TableCell>
                      {formatNumberWithTooltip(calculatedValue, 8)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            )}
          </DataTable>
        </div>
      </div>
    </div>
  );
};

export interface TotalUnitInterestCardProps {
  slotName: string;
  items: UnitInterestTableItem[];
  calculatedValue: number;
  calculationType: CalculationType;
  onTractClick?: (tractId: string, subtractId?: string) => void;
  title?: UnitCalculationContentProps['title'];
  lineFormula?: UnitCalculationContentProps['lineFormula'];
}

export const TotalUnitInterestCard = ({
  slotName,
  items,
  calculatedValue,
  calculationType,
  onTractClick,
  title,
  lineFormula,
}: TotalUnitInterestCardProps) => {
  const renderTrigger = (active: boolean, toggle: () => void) => (
    <CalculationIconTrigger
      definition="Show Calculation"
      active={active}
      onClick={toggle}
    >
      <div className={styles.iconContainer}>
        <Calculation aria-hidden="true" role="img" />
      </div>
    </CalculationIconTrigger>
  );

  return (
    <BaseCard
      contentClassName={styles.cardContent}
      renderTrigger={renderTrigger}
      content={
        <UnitCalculationContent
          slotName={slotName}
          items={items}
          calculatedValue={calculatedValue}
          calculationType={calculationType}
          onTractClick={onTractClick}
          title={title}
          lineFormula={lineFormula}
        />
      }
    />
  );
};
